/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { ViewBase } from '../view-base.js';
import { projectCreateViewStyles } from './project-create-view-styles.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-image-upload-input/q1-image-upload-input.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== Q1 MODULES ===*/
import { projectVisibilityOptions } from '../../models/project-client-enums.js';
import { q1DocFunctions } from '../../modules/q1-core-functions.js';

/*=== CONSTANTS ===*/

// None yet.

/*=== VIEW CLASS ===*/

export class ProjectCreateView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    projectCreateViewStyles,
  ];

  static properties = {
    ...super.properties,

    name: { type: String },
    colorPrimary: { type: String },
    colorSecondary: { type: String },
    visibility: { type: String },
    avatar_newUploadPath: { type: String },

    loading: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();

    this.name = '';
    this.colorPrimary = '';
    this.colorSecondary = '';
    this.visibility = 'link';
    this.avatar_newUploadPath = '';

    this.projectVisibilityOptions = [];
    
    this.loading = false;
    this.error = false;
    this.errorMessage = '';
  }

  /*=== LIFECYCLE ===*/
  
  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();
  }

  /*=== ACTIONS ===*/

  createProject() {
    const newProjectForm = this.renderRoot.querySelector('#new-project-form');
    
    if (!newProjectForm.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }
    
    const { createDoc } = q1DocFunctions;
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    
    createDoc({
      docCollection: 'projects',
      docData: {
        name: this.name,
        colorPrimary: this.colorPrimary,
        colorSecondary: this.colorSecondary,
        visibility: this.visibility,
        avatar_newUploadPath: this.avatar_newUploadPath,
      },
    }).then((response) => {
      this.showToast(
        'Navigating you to your new project page.', 'Project created successfully!', 'success'
      );
      
      const projectKey = response.data.docData?.key;
      this.navigateTo(`/projects/${projectKey}`);
    }).catch((error) => {
      console.error('Error calling function:', error);
      this.showToast(error.message, 'There was an error creating your project!', 'danger');
      this.errorMessage = error.message;
    }).finally(() => {
      this.loading = false;
    });
  }

  /*=== HANDLERS ===*/
  
  handleProjectFormSubmit(event) {
    // IMPROVEME: We're getting an "invalid form control... not focusable" error when the create
    // project logic is run in the context of a form submit rather than a button click.
    // Not sure how to resolve this. THIS ISN'T RUNNING RIGHT NOW. Leaving it here for now.

    event.preventDefault();
  }

  /*=== GETTERS ===*/

  get visibilityRadioButtonsTemplate_withDisabled() {
    return html`
      ${projectVisibilityOptions.map((option) => html`
        <sl-tooltip content=${option.description}>
          <sl-radio-button
            value=${option.value}
            ?disabled=${!option.availableAt?.includes('created')}
          >
            <sl-icon
              slot="prefix"
              library=${option.iconLibrary}
              name=${option.icon}
            ></sl-icon>
            ${option.label}
          </sl-radio-button>
        </sl-tooltip>
      `)}
    `;
  }

  /*=== TEMPLATES ===*/

  get contentTemplate() {
    return html`
      <header-bar class="with-border">
        <h1>Create a New Project</h1>

        <sl-icon library="material" name="business_center" slot="icon" class="header-icon">
        </sl-icon>
      </header-bar>

      ${!this.error ? '' : html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.errorMessage}
        </sl-alert>
      `}

      <sl-alert variant="neutral" open>
      <p class="subtitle primary">Community & Collaboration are at the heart of the q1 Network 
        experience: You don't need to create in a silo! Join the <a href="https://q1.is/964e7c" 
        target="_blank">Project Organizers Guild</a> to connect with other project leaders, get 
        support and stay up-to-date with the best tips & tricks for maximizing your q1 gains.</p>
      </sl-alert>

      <form id="new-project-form" class="q1">
        <field-pair>
          <sl-input
            id="name"
            name="name"
            label="Project Name"
            value=${this.name}
            @sl-change=${this.handleChange}
            maxlength="50"
            required
          ></sl-input>

          <q1-image-upload-input
            id="avatar_newUploadPath"
            name="avatar_newUploadPath"
            upload-folder="project-avatar"
            @change="${this.handleChange}"
            label="Project Avatar"
            help-text="Image icon representing the project"
            required
          ></q1-image-upload-input>
        </field-pair>

        <field-pair>
          <input-wrapper>
            <label for="colorPrimary">Primary Project Color</label>
            <sl-color-picker
              id="colorPrimary"
              name="colorPrimary"
              label="Project Color"
              @sl-change=${this.handleChange}
              size="large"
              swatches="#D81D39; #DB3A1A; #DE6616; #E2B013; #9DBC38; #53A84D; 
                #4AAB99; #488BAD; #4864AD; #7D52A3; #A74E9A; #C62F81;"
              required
            ></sl-color-picker>
          </input-wrapper>
          
          <input-wrapper>
            <label for="colorSecondary">Secondary Project Color</label>
            <sl-color-picker
              id="colorSecondary"
              name="colorSecondary"
              label="Project Color"
              @sl-change=${this.handleChange}
              size="large"
              swatches="#D81D39; #DB3A1A; #DE6616; #E2B013; #9DBC38; #53A84D; 
                #4AAB99; #488BAD; #4864AD; #7D52A3; #A74E9A; #C62F81;"
              required
            ></sl-color-picker>
          </input-wrapper>
        </field-pair>
        
        <sl-radio-group
          id="visibility"
          name="visibility"
          label="Project Visibility"
          value=${this.visibility}
          @sl-change=${this.handleChange}
          required
        >
          <span slot="help-text">
            You'll need to finish activating your project before it can be 
            made more publicly visible.
          </span>

          ${this.visibilityRadioButtonsTemplate_withDisabled}
        </sl-radio-group>
        
        <button-bar class="right-align">
          <sl-button
            variant="primary"
            size="large"
            @click=${this.createProject}
            ?disabled=${this.loading}
          >
            <sl-icon slot="prefix" library="material" name="add_circle"></sl-icon>
            Create Project
          </sl-button>
        </button-bar>
      </form>

      <sl-divider></sl-divider>
      
      <h2>Quick tips on project creation</h2>

      <ul class="icon-list">
        <li>
          <icon-frame class="primary">
            <sl-icon library="material" name="public"></sl-icon>
          </icon-frame>
          <span>
            <strong>Basic project info is <em>always</em> publicly accessible</strong><br/>
            The basic project identity info (name, avatar, headline, colors) will always be 
            accessible in some form on the public internet, even if it's not readily discoverable.
            So please don't put anything confidential in those fields.
          </span>
        </li>
        
        <li>
          <icon-frame class="primary">
            <sl-icon library="material" name="visibility_off"></sl-icon>
          </icon-frame>
          <span>
            <strong>Project content is never on the public internet</strong><br/>
            Setting a project to public visibility only means that the project can be found on the 
            q1 Network website without signing in. The membership & content of the project is 
            only visible to authenticate q1 Network members, based on the visibility settings.
          </span>
        </li>
        
        <li>
          <icon-frame class="primary">
            <sl-icon library="material" name="rocket_launch"></sl-icon>
          </icon-frame>
          <span>
            <strong>Next Step: The project activation flow</strong><br/>
            After creating the project, we'll walk you through the activation flow.
            This includes finishing the project setup, inviting members, and connecting your 
            project with guilds. You'll unlock more features and wider visibility as you go.
          </span>
        </li>
      </ul>

      <q1-spinner-panel label="Processing..." ?open=${this.loading}></q1-spinner-panel>
    `;
  }
}

customElements.define('project-create-view', ProjectCreateView);
